import { UTCDate } from '@date-fns/utc';
import {
    addMonths,
    format,
    isAfter,
    isBefore,
    isSameDay,
    lastDayOfWeek,
    startOfDay,
    startOfWeek,
} from 'date-fns';
import { sv } from 'date-fns/locale';
import { generatePeriodText } from 'utils/utils';
import { CalendarDateRange } from './types';

export interface Month {
    year: number;
    month: number;
}

interface InitialMonthOptions {
    periods: CalendarDateRange[];
    initialMonth?: Month;
    disableBefore?: UTCDate;
    disableAfter?: UTCDate;
}

/** Calendar dates use UTC calendar fields; normalize without reinterpreting them locally. */
export function normalizeDate(date: UTCDate): UTCDate {
    return startOfDay(date);
}

export function normalizeRange(
    start: UTCDate,
    end: UTCDate
): CalendarDateRange {
    const normalizedStart = normalizeDate(start);
    const normalizedEnd = normalizeDate(end);

    if (normalizedStart.getTime() <= normalizedEnd.getTime()) {
        return {
            start: normalizedStart,
            end: normalizedEnd,
        };
    }

    return {
        start: normalizedEnd,
        end: normalizedStart,
    };
}

export function getWeekRange(day: UTCDate): CalendarDateRange {
    return normalizeRange(
        startOfWeek(day, { weekStartsOn: 1 }),
        lastDayOfWeek(day, { weekStartsOn: 1 })
    );
}

export function toMonth(date: UTCDate): Month {
    const normalized = normalizeDate(date);

    return {
        year: normalized.getFullYear(),
        month: normalized.getMonth(),
    };
}

export function compareMonths(a: Month, b: Month): number {
    if (a.year !== b.year) {
        return a.year - b.year;
    }

    return a.month - b.month;
}

export function clampMonth(
    month: Month,
    disableBefore?: UTCDate,
    disableAfter?: UTCDate
): Month {
    let nextMonth = month;

    if (disableBefore) {
        const firstAllowedMonth = toMonth(disableBefore);
        if (compareMonths(nextMonth, firstAllowedMonth) < 0) {
            nextMonth = firstAllowedMonth;
        }
    }

    if (disableAfter) {
        const lastAllowedMonth = toMonth(disableAfter);
        if (compareMonths(nextMonth, lastAllowedMonth) > 0) {
            nextMonth = lastAllowedMonth;
        }
    }

    return nextMonth;
}

export function getInitialMonth({
    periods,
    initialMonth,
    disableBefore,
    disableAfter,
}: InitialMonthOptions): Month {
    if (initialMonth) {
        return clampMonth(initialMonth, disableBefore, disableAfter);
    }

    if (periods.length > 0) {
        return clampMonth(
            toMonth(periods[0].start),
            disableBefore,
            disableAfter
        );
    }

    const today = new Date();
    return clampMonth(
        { year: today.getFullYear(), month: today.getMonth() },
        disableBefore,
        disableAfter
    );
}

export function shiftMonth(month: Month, monthDelta: number): Month {
    const nextDate = addMonths(
        new UTCDate(month.year, month.month, 1),
        monthDelta
    );

    return toMonth(nextDate);
}

export function isDayDisabled(
    day: UTCDate,
    disableBefore?: UTCDate,
    disableAfter?: UTCDate
): boolean {
    const normalizedDay = normalizeDate(day);

    if (disableBefore) {
        const lowerBound = normalizeDate(disableBefore);
        if (isBefore(normalizedDay, lowerBound)) {
            return true;
        }
    }

    if (disableAfter) {
        const upperBound = normalizeDate(disableAfter);
        if (isAfter(normalizedDay, upperBound)) {
            return true;
        }
    }

    return false;
}

export function isMonthSelectable(
    year: number,
    month: number,
    disableBefore?: UTCDate,
    disableAfter?: UTCDate
): boolean {
    const monthStart = new UTCDate(year, month, 1);
    const monthEnd = new UTCDate(year, month + 1, 0);

    if (disableBefore) {
        const lowerBound = normalizeDate(disableBefore);
        if (isBefore(monthEnd, lowerBound)) {
            return false;
        }
    }

    if (disableAfter) {
        const upperBound = normalizeDate(disableAfter);
        if (isAfter(monthStart, upperBound)) {
            return false;
        }
    }

    return true;
}

export function isRangeWithinBounds(
    range: CalendarDateRange,
    disableBefore?: UTCDate,
    disableAfter?: UTCDate
): boolean {
    const normalizedRange = normalizeRange(range.start, range.end);

    if (disableBefore) {
        const lowerBound = normalizeDate(disableBefore);

        if (isBefore(normalizedRange.start, lowerBound)) {
            return false;
        }
    }

    if (disableAfter) {
        const upperBound = normalizeDate(disableAfter);

        if (isAfter(normalizedRange.end, upperBound)) {
            return false;
        }
    }

    return true;
}

export function isWeekSelectable(
    day: UTCDate,
    disableBefore?: UTCDate,
    disableAfter?: UTCDate
): boolean {
    return isRangeWithinBounds(getWeekRange(day), disableBefore, disableAfter);
}

export function getVisibleYears(
    anchorYear: number,
    disableBefore?: UTCDate,
    disableAfter?: UTCDate
): number[] {
    const minYear = disableBefore
        ? normalizeDate(disableBefore).getFullYear()
        : anchorYear - 10;
    const maxYear = disableAfter
        ? normalizeDate(disableAfter).getFullYear()
        : anchorYear + 10;

    const years: number[] = [];
    for (let year = minYear; year <= maxYear; year++) {
        years.push(year);
    }

    return years;
}

export function formatPeriodValue(period: CalendarDateRange | null): string {
    if (!period) {
        return '';
    }

    const { start, end } = normalizeRange(period.start, period.end);
    if (isSameDay(start, end)) {
        return format(start, 'd MMMM yyyy', { locale: sv });
    }

    const { startValue, endValue } = generatePeriodText(start, end);

    return `${startValue} - ${endValue}`;
}
